/**
 * handTicketsOver — RPC contract for the handTicketsOver callable: the holder
 * of an access link names a person and gives them some of the tickets that
 * link carries.
 *
 * The capability is the token, not a session. An OrderAccess doc
 * (order-access.entity.ts) is reached by possession of its path alone,
 * because a buyer has no account to authenticate with. This callable resolves
 * the supplied token to that doc and refuses any ticket absent from its own
 * `tickets[]` — that membership check is the whole authorization, and it
 * widens no access OrderAccess doesn't already grant: the caller gives away
 * only tickets they already hold and already see.
 *
 * The field is `accessToken`, not `orderAccessToken`. An order's token and a
 * slice's token take the identical path here — mintWalletSaveLinks.action.ts
 * names its own field for the order because an order's token is all it ever
 * receives, while this call receives either kind and treats them the same.
 * That sameness is what lets a recipient hand a ticket on again with no
 * second rule behind it: their slice doc answers the membership check exactly
 * as the buyer's order doc does.
 *
 * Idempotent on (token, sorted ticketIds): the same set under the same name
 * returns the standing link, a different name rotates it. Handing a ticket on
 * supersedes the caller's own record for it — the ticket leaves the caller's
 * slice and any slice downstream of it dies — so exactly one live handover
 * per ticket survives the call, and the door has exactly one answer to who
 * holds a ticket.
 *
 * Fields justified:
 *   accessToken — the OrderAccess capability token the caller holds, an
 *                 order's or a slice's; see the module docstring above.
 *   ticketIds   — which of that doc's tickets travel. A slice is 1..N because
 *                 a couple's two tickets are one handover to one person, and
 *                 keying a record per ticket would make that unrepresentable.
 *   name        — who holds these tickets now; typed from Handover's own
 *                 field, so the length the callable refuses at and the length
 *                 the record stores under are one ceiling, never two free to
 *                 drift into storing a truncation of what was accepted.
 *   sentVia     — the channel this handover travels by; typed from
 *                 Handover's own field, the one source for that vocabulary.
 *   recipientEmail — where the deployment itself sends the link on the mail
 *                 channel. Carried by this call and stored by nothing it
 *                 writes, so a later send asks the buyer for it again.
 * Excluded:
 *   parentHandoverId — the chain edge is the caller's own token, which the
 *                 handler already resolved; a caller-asserted parent would
 *                 let the chain claim an ancestry the tokens contradict.
 *   createdAt   — the server stamps its own write; a client clock is not
 *                 evidence of when anything happened.
 *
 * Output fields justified:
 *   url        — the link the recipient opens, composed from the
 *                deployment's own web origin and never stored; the caller's
 *                message carries it.
 *   handoverId — the record this call wrote, and the name takeTicketsBack
 *                reclaims it by.
 *   mailAccepted — whether the mail provider took the link for sending. A
 *                refused mail is not a refused handover: the record is
 *                written, the slice token minted and the link live either
 *                way, so the send's outcome rides on the successful output
 *                beside the very link the caller falls back to. Null is the
 *                third fact — no mail was attempted, which the channel
 *                decides — held apart from false so this output alone
 *                answers whether a send happened, without the caller
 *                re-reading what it asked for.
 * Output excluded:
 *   delivered / opened — a handover's carriers report nothing back, so an
 *                arrival fact here could only ever be a claim; what the
 *                provider took is the whole of what this call witnesses.
 *   error / reason — a caller shown a refused send offers the link to copy,
 *                which is the same offer under every reason; a message
 *                carried here would be operator diagnostics rendered to a
 *                buyer.
 *
 * Organization context is explicit in the request. The server validates
 * membership or possession of the scoped access token independently.
 */
import { z } from "zod";
import { HandoverSchema } from "./handover.entity.ts";
import { SecretToken } from "./ticket.entity.ts";
import { Id } from "./workspace.entity.ts";

export const HandTicketsOverInput = z
	.object({
		orgId: Id,
		accessToken: SecretToken.describe(
			"the OrderAccess capability token the caller holds — an order's or a slice's, treated identically; possession alone authorizes this call over the tickets that token's own document carries",
		),
		ticketIds: z
			.array(z.string().min(1))
			.min(1)
			.describe(
				"the tickets travelling together to one person, every one of them named by the caller's own OrderAccess document",
			),
		name: HandoverSchema.shape.name,
		sentVia: HandoverSchema.shape.sentVia,
		recipientEmail: z
			.string()
			.email()
			.nullish()
			.describe(
				"the address the deployment sends this handover's link to, as the buyer typed it — carried on the mail channel and absent on every other. It is transmitted and never stored: no handover record and no slice document holds it, so sending the same link again asks the buyer for the address again. Keeping a recipient's address off a document every scanning account lists is what makes that the shape rather than a convenience to be undone",
			),
	})
	.strict();
export type HandTicketsOverInput = z.infer<typeof HandTicketsOverInput>;

export const HandTicketsOverOutput = z
	.object({
		url: z
			.string()
			.url()
			.describe(
				"the access link that opens exactly these tickets, carrying the same powers over them the caller had",
			),
		handoverId: z
			.string()
			.min(1)
			.describe("the handover record this call wrote"),
		mailAccepted: z
			.boolean()
			.nullish()
			.describe(
				"whether the mail provider took this handover's link for sending. Null is no mail attempted at all, a different fact from a send the provider refused, and the channel the handover travels by is what decides which of the two applies. It states what the provider accepted, never that anything arrived: no mailbox is observed by this call, and the handover stands either way — the link is live and the caller can carry it themselves",
			),
	})
	.strict();
export type HandTicketsOverOutput = z.infer<typeof HandTicketsOverOutput>;
